import { useCallback } from 'react';
import { useSelector } from 'react-redux';

// Redux
import { selectors as authSelector } from 'src/features/auth/authSlice';
import { actions, selectors as userSelectors } from 'src/features/users/usersSlice';
import store, { useAppDispatch } from 'src/store';
import { changeSettingNotification, getAll, getOne, remove, update } from '../services';

// Hooks
import useRole from 'src/features/auth/hooks/useUserRoles';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag/useFeatureFlag';

// Enums
import { FeatureFlagKey, RoleEnum } from 'src/ts/enums';

// Constants
import { LeasingTeam, RolesValueLabel, SnapptTeam } from 'src/constants/roles';

// Types
import { IProperty, IUser, RoleType } from 'src/ts/interfaces';

const useUsers = () => {
  const dispatch = useAppDispatch();
  const {
    isBuildingManager,
    isCompanyAdmin,
    isAdmin,
    isAccountRepresentative,
    isAdminOrAccountRepresentative,
    isPartnerAdmin
  } = useRole();
  const isPartnerAdminRoleEnabled = useFeatureFlag(FeatureFlagKey.AIC_1285_PARTNER_ADMIN_ROLE);
  const userResponse = useSelector(authSelector.user);

  const currentFilter = userSelectors.getAll.filter(store.getState());

  const activeRolesValueLabel = RolesValueLabel.filter((role) => {
    if (!isAdminOrAccountRepresentative) {
      if (
        [RoleEnum.SeniorFraudAnalyst, RoleEnum.FraudAnalyst, RoleEnum.FdeManager].includes(
          role.value as RoleType
        )
      ) {
        return false;
      }
    }

    if (!isAdminOrAccountRepresentative && role.value === RoleEnum.AccountRepresentative) {
      return false;
    }

    // The single place the flag lives: Admin's ListRoles branch returns this
    // whole list, so without it every actor would see the role the moment
    // ROLE_DESCRIPTION has a label for it.
    if (!isPartnerAdminRoleEnabled && role.value === RoleEnum.PartnerAdmin) {
      return false;
    }
    return true;
  });

  const onGetAll = (page?: number, rowsPerPage?: number, filter?: string, isDDown?: boolean) => {
    const filters = filter === undefined ? currentFilter.filter : filter;
    const pages = page === undefined ? currentFilter.page : page;
    const rowsPerPages = rowsPerPage === undefined ? currentFilter.rowsPerPage : rowsPerPage;

    dispatch(
      getAll({ ...currentFilter, filter: filters, page: pages, rowsPerPage: rowsPerPages, isDDown })
    );
  };

  const onGetUser = (id?: string) => {
    if (!id) throw new Error("Is missing the user identifier to get the user's information");
    dispatch(getOne(id));
  };

  const onSave = (dataForm: IUser) => {
    if (dataForm && dataForm.id) {
      dispatch(update({ userId: dataForm.id, data: dataForm }));
    }
  };

  const onResetUpdate = () => {
    dispatch(actions.resetSave());
  };

  const onRemove = (id?: string) => {
    if (!id) throw new Error('Is missing the user identifier to remove the user');
    dispatch(remove(id));
  };

  const onHandleSettingNotifications = (user: IUser) => {
    if (!user.id)
      throw new Error('Is missing the user identifier to change the setting notification');
    dispatch(
      changeSettingNotification({ ...user, settings_notification: !user.settings_notification })
    );
  };

  const onResetRemove = (page?: number, rowsPerPage?: number, filter?: string) => {
    dispatch(actions.resetRemove());
    onGetAll(page, rowsPerPage, filter);
  };

  const extractPropertyNames = (properties: IProperty[]) => {
    return properties.map((property: IProperty) => property.name).join(', ');
  };

  const ListRoles = () => {
    switch (userResponse?.role) {
      case RoleEnum.Admin:
        return activeRolesValueLabel;

      case RoleEnum.AccountRepresentative:
        return activeRolesValueLabel.filter((item) => LeasingTeam.includes(item.value as RoleType));

      case RoleEnum.CompanyAdmin:
      case RoleEnum.BuildingManager:
        return activeRolesValueLabel.filter((item) =>
          [
            RoleEnum.BuildingManager,
            RoleEnum.PropertyAdmin,
            RoleEnum.OnSiteRepresentative
          ].includes(item.value as RoleType)
        );

      // Invites its own role and nothing else, mirroring the server-side
      // @allowed_invitation_roles entry. The filter above empties this when the
      // flag is off, so no separate flag check is needed here.
      case RoleEnum.PartnerAdmin:
        return activeRolesValueLabel.filter((item) => item.value === RoleEnum.PartnerAdmin);

      default:
        return [];
    }
  };

  const showCompanySelectorFromRole = (role: RoleType) => {
    if (SnapptTeam.includes(role) || role === RoleEnum.Owner) return false;
    return true;
  };

  const showOwnerSelectorFromRole = (role: RoleType) => {
    return role === RoleEnum.Owner;
  };

  const showPropertySelectorFromRole = (role: RoleType) => {
    if (!showCompanySelectorFromRole(role) || [RoleEnum.CompanyAdmin].includes(role)) return false;
    return true;
  };

  const showRemoveFromRole = (role: RoleType, company_id?: string) => {
    if (isBuildingManager)
      return role === RoleEnum.PropertyAdmin || role === RoleEnum.OnSiteRepresentative;
    if (isCompanyAdmin) return company_id === userResponse?.company_id;
    // A partner requests a deletion rather than performing one, so the control
    // never renders. The API refuses the action as well.
    if (isPartnerAdmin) return false;
    if (isAdmin || isAccountRepresentative) return true;
    return false;
  };

  // Edit is still allowed, but only on same-role peers in the same company,
  // which is the rule the API enforces. Unchanged for every other role.
  const showEditFromRole = (role: RoleType, company_id?: string) => {
    if (isPartnerAdmin)
      return role === RoleEnum.PartnerAdmin && company_id === userResponse?.company_id;
    return true;
  };

  const showCompanyInformation = (role: RoleType) => {
    if (SnapptTeam.includes(role)) return false;
    return true;
  };

  const showPropertyInformation = (role: RoleType) => {
    if ([...SnapptTeam, RoleEnum.CompanyAdmin].includes(role)) return false;
    return true;
  };

  const showTeamSelector = (role: RoleType) => {
    return [
      RoleEnum.FraudAnalyst,
      RoleEnum.FdeManager,
      RoleEnum.SeniorFraudAnalyst,
      RoleEnum.AccountRepresentative
    ].includes(role);
  };

  const allowAddUser = () => {
    if (!userResponse?.role) return false;
    if (
      [
        RoleEnum.Admin,
        RoleEnum.BuildingManager,
        RoleEnum.CompanyAdmin,
        RoleEnum.AccountRepresentative
      ].includes(userResponse?.role)
    )
      return true;
    // Gated so the Invite User button and the role dropdown appear together.
    if (userResponse?.role === RoleEnum.PartnerAdmin) return isPartnerAdminRoleEnabled;
    return false;
  };

  const onSearch = (q: string, filter?: string) => {
    const currentFilter = userSelectors.getAll.filter(store.getState());
    const search = encodeURIComponent(q);
    dispatch(getAll({ ...currentFilter, filter, page: 0, q: search }));
  };

  const onSort = (query: string, filter?: string) => {
    dispatch(getAll({ ...currentFilter, filter, page: 0, sort: query }));
  };

  const onResetGetUser = useCallback(() => {
    dispatch(actions.resetGetOne());
  }, [dispatch]);

  const onResetGetAll = useCallback(() => {
    dispatch(actions.resetGetAll());
  }, [dispatch]);

  return {
    onGetAll,
    onResetGetAll,
    onGetUser,
    onSave,
    onResetUpdate,
    ListRoles,
    onRemove,
    onHandleSettingNotifications,
    onResetRemove,
    showCompanySelectorFromRole,
    showPropertySelectorFromRole,
    showOwnerSelectorFromRole,
    showRemoveFromRole,
    showEditFromRole,
    showCompanyInformation,
    showPropertyInformation,
    showTeamSelector,
    allowAddUser,
    extractPropertyNames,
    onSearch,
    onSort,
    userResponse,
    onResetGetUser,
    currentFilter
  };
};

export default useUsers;
